/**
 * GameContext - manages persistent game state throughout the application's lifetime.
 * Persists across world transitions, coordinates world/game mode lifecycle, and manages
 * multiplayer connections (client and dedicated server modes).
 */
import { NetRuntime } from '../multiplayer/NetRuntime.js';
import { AssetPath } from '../storage/storageProvider.js';
import { type Constructor, EngineClass } from '../systems/ClassRegistry.js';
import { ObjectNamingSystem } from '../systems/ObjectNamingSystem.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';
import { WorldSerializer } from '../systems/WorldSerialization.js';
import { extractMapNameFromPath } from '../utils/mapNameExtractor.js';
import { resourceManager } from '../utils/ResourceManager.js';
import { spawn } from '../utils/Spawn.js';
import { shortUUID } from '../utils/uuid.js';

import { GameMode, type GameModeOptions } from './GameMode.js';
import { Level } from './Level.js';
import { World, type WorldOptions } from './World.js';

import type { BaseGameLoop } from './GameLoop.js';
import type { NetSettings } from '../multiplayer/NetWorld.js';

export interface GameContextConfig {
  /** Initial world/scene path to load on startup. If omitted, creates an empty world */
  initialWorldPath?: AssetPath;

  /** Default scene path to reload when disconnecting from multiplayer server */
  defaultScenePath?: AssetPath;

  /** Default GameMode class to instantiate when scene doesn't specify one */
  defaultGameModeClass?: Constructor<GameMode>;

  /** Options passed to the default GameMode initialize method */
  defaultGameModeOptions?: GameModeOptions;

  /** Networking settings for multiplayer client/server runtime. */
  netSettings?: NetSettings;
}

@EngineClass('GameContext')
export class GameContext {
  public readonly uuid: string = shortUUID();
  public readonly name: string;

  protected config: GameContextConfig;
  protected world: World | null = null;
  protected currentLevel: Level | null = null;
  protected gameLoopRef: WeakRef<BaseGameLoop> | null = null;
  /** Scene file used for co-located resources (e.g. build-data zip). */
  protected activeScenePath: AssetPath | null = null;

  constructor(config: GameContextConfig = {}) {
    ObjectTracker.track(this);
    this.config = config;
    this.name = ObjectNamingSystem.generateName(this);
  }

  public setGameLoop(gameLoop: BaseGameLoop): void {
    this.gameLoopRef = new WeakRef(gameLoop);
  }

  public getGameLoop(): BaseGameLoop | undefined {
    return this.gameLoopRef?.deref();
  }

  public getWorld(): World | null {
    return this.world;
  }

  public getLevel(): Level | null {
    return this.currentLevel;
  }

  public getNetSettings(): NetSettings {
    return this.config.netSettings ?? {};
  }

  public getActiveScenePath(): AssetPath | null {
    return this.activeScenePath;
  }

  public setActiveScenePath(path: AssetPath | null): void {
    this.activeScenePath = path;
  }

  /**
   * One-time startup sequence. Called by BaseGameLoop during start(). Do not call more than once.
   * For subsequent map changes use loadLevel() or connectToServer().
   */
  public async startGameContext(worldOptions: WorldOptions): Promise<void> {
    console.log('[GameContext] Starting...');

    const netSettings = this.getNetSettings();

    if (netSettings.serverUrl) {
      // Connecting to server - create empty world first (no gameMode, server provides game state)
      console.log('[GameContext] Client mode - creating empty world before connecting to server');
      worldOptions.gameMode = undefined; // Will be null
      this.world = await this.createWorld(worldOptions);
      this.setWorldOnGameLoop();
      // Now connect to server
      await this.connectToServer(netSettings.serverUrl);
    } else if (this.config.initialWorldPath) {
      // Loading local scene - extract gameMode from scene file
      console.log(`[GameContext] Loading initial level from ${this.config.initialWorldPath}`);
      this.setActiveScenePath(this.config.initialWorldPath);
      const sceneData = await resourceManager.loadJsonObject(this.config.initialWorldPath);

      // Always create a game mode in non-server startup.
      // Prefer scene-defined game mode, otherwise use configured default or base GameMode.
      worldOptions.gameMode = this.resolveStartupGameMode(sceneData);

      this.world = await this.createWorld(worldOptions);
      this.setWorldOnGameLoop();

      // Load the scene data directly into the world
      this.currentLevel = await WorldSerializer.loadWorld(this.world, sceneData);
      this.onLevelLoaded(this.currentLevel);

      // Start server if running on server and port range is configured
      if (NetRuntime.isDedicatedServer() && netSettings.serverPortRange) {
        const port = await this.world.netWorld.startServer(netSettings.serverPortRange, {
          lagSimulationConfig: netSettings.serverLagSimulationConfig
        });
        console.log(`[GameContext] ✓ Server listening on port ${port}`);
        console.log(`[GameContext] ✓ Clients can connect to: ws://localhost:${port}`);

        // Now that server is started, set the map name so clients know what to load
        console.log(`[GameContext] initialWorldPath: ${this.config.initialWorldPath?.initialPath ?? 'undefined'}`);
        const mapName = extractMapNameFromPath(this.config.initialWorldPath);
        console.log(`[GameContext] Setting map name to: ${mapName}`);
        if (mapName) {
          this.world.netWorld.setMapName(mapName);
          // Signal that the initial scene is ready so any clients that connect
          // immediately after the server binds are not stuck in the deferred join queue.
          this.world.netWorld.onServerMapReady();
        }
      }
    } else {
      // No initial scene or server - just create empty world
      console.log('[GameContext] Creating empty world');
      worldOptions.gameMode ??= this.createDefaultGameMode();
      this.world = await this.createWorld(worldOptions);
      this.setWorldOnGameLoop();
    }
  }

  protected resolveStartupGameMode(sceneData: any): GameMode {
    const sceneGameMode = WorldSerializer.extractGameMode(sceneData);
    if (sceneGameMode) {
      console.log('[GameContext] Using gameMode from scene:', sceneGameMode.constructor.name);
      return sceneGameMode;
    }

    return this.createDefaultGameMode();
  }

  protected createDefaultGameMode(): GameMode {
    const GameModeClass = this.config.defaultGameModeClass ?? GameMode;
    const gameMode = spawn(GameModeClass, this.config.defaultGameModeOptions);
    console.log('[GameContext] Using default gameMode:', gameMode.constructor.name);
    return gameMode;
  }

  private setWorldOnGameLoop(): void {
    const gameLoop = this.getGameLoop();
    if (gameLoop && this.world) {
      gameLoop.setWorld(this.world);
    }
  }

  public async connectToServer(serverUrl: string): Promise<void> {
    console.log(`[GameContext] Connecting to server: ${serverUrl}`);

    const gameLoop = this.getGameLoop();
    if (!gameLoop) {
      throw new Error('[GameContext] Cannot connect to server: no game loop available');
    }

    await gameLoop.connectToServer(serverUrl);
  }

  /**
   * Creates a new World instance. Override to customize world creation.
   */
  protected async createWorld(options: WorldOptions): Promise<World> {
    console.log('[GameContext] Creating world...');
    if (!options.netWorld) {
      console.warn('[GameContext] No netWorld provided in options - this may cause replication issues');
    }
    const world = new World(options);
    await world.createPhysicsEngine();
    return world;
  }

  /**
   * Loads a level from a file path, replacing the current level content.
   * Use for level transitions within the same world. For complete world transitions, use travelToWorld().
   */
  public async openLevel(path: AssetPath): Promise<Level> {
    console.log(`[GameContext] Opening level: ${path}`);

    const gameLoop = this.getGameLoop();
    if (!gameLoop) {
      throw new Error('[GameContext] Cannot open level: no game loop available');
    }

    // Unload previous level
    if (this.currentLevel) {
      console.log(`[GameContext] Unloading previous level with ${this.currentLevel.actors.length} actors`);
      this.currentLevel.unload();
    }

    // Delegate loading to the game loop (engine implementation)
    this.currentLevel = await gameLoop.loadLevelDataFromPath(path);

    // Notify subclasses
    this.onLevelLoaded(this.currentLevel);

    return this.currentLevel;
  }

  protected onLevelLoaded(level: Level): void {
    console.log(`[GameContext] Level loaded with ${level.actors.length} actors`);
  }

  /**
   * Performs a complete world transition, destroying the current world and creating a new one.
   * Use for major transitions requiring fresh world state (e.g., changing game modes).
   */
  public async travelToWorld(worldPath: AssetPath, worldOptions: WorldOptions): Promise<World> {
    console.log(`[GameContext] Traveling to world: ${worldPath}`);

    // Tear down current world
    await this.destroyCurrentWorld();

    // Create new world
    this.world = await this.createWorld(worldOptions);

    // Load the world data via openLevel
    await this.openLevel(worldPath);

    return this.world;
  }

  protected async destroyCurrentWorld(): Promise<void> {
    if (!this.world) {
      return;
    }

    console.log('[GameContext] Destroying current world...');

    // End play if playing
    if (this.world.isPlaying()) {
      this.world.endPlay();
    }

    // Clear level reference
    this.currentLevel = null;

    // Destroy world
    this.world.destroy();
    this.world = null;
  }

  /**
   * Full world transition back to standalone after disconnecting from a multiplayer server.
   * Destroys the current world and creates a fresh one. Uses defaultScenePath if set,
   * otherwise falls back to initialWorldPath.
   */
  public async reloadInitialScene(): Promise<void> {
    const scenePathToReload = this.config.defaultScenePath ?? this.config.initialWorldPath;
    if (!scenePathToReload) {
      console.warn('[GameContext] No scene path configured for reload, cannot reload scene');
      return;
    }

    const gameLoop = this.getGameLoop();
    if (!gameLoop) {
      throw new Error('[GameContext] Cannot reload scene: no game loop available');
    }

    console.log(`[GameContext] Transitioning to standalone world from ${scenePathToReload}`);

    this.setActiveScenePath(scenePathToReload);

    // Load scene data first (before destroying anything).
    const sceneData = await resourceManager.loadJsonObject(scenePathToReload);
    const gameMode = this.resolveStartupGameMode(sceneData);

    // Tear down the current (server-connected) world.
    await this.destroyCurrentWorld();

    // Build a fresh world.  getWorldConfiguration() now returns the StandaloneNetWorld
    // that BaseGameLoop already swapped in during disconnectFromServer().
    this.world = await this.createWorld({
      ...gameLoop.getWorldConfiguration(),
      gameMode,
      gameLoop: new WeakRef(gameLoop),
    });
    this.setWorldOnGameLoop();

    // Load actors into the new world (no beginPlay yet — caller handles subsystem setup).
    this.currentLevel = await WorldSerializer.loadWorld(this.world, sceneData);
    this.onLevelLoaded(this.currentLevel);
    console.log('[GameContext] ✓ Standalone world created and scene loaded');
  }

  public async shutdown(): Promise<void> {
    console.log('[GameContext] Shutting down...');
    await this.destroyCurrentWorld();
  }
}
